import React, { useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff } from 'lucide-react';
import SecurityIdleStep from '@/components/security/SecurityIdleStep';
import ChangePasswordStep from '@/components/security/ChangePasswordStep';
import TwoFASetupStep from '@/components/security/TwoFASetupStep';
import TwoFADisableStep from '@/components/security/TwoFADisableStep';

const STEPS = { IDLE: 'idle', CHANGE_PWD: 'change-password', SETUP: 'setup', DISABLE: 'disable' };

export default function SecurityModal() {
  const { user, token, login } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(STEPS.IDLE);
  const [qrData, setQrData] = useState(null);
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  const refreshUser = useCallback(async () => {
    const resp = await axios.get(`${API}/auth/me`);
    login(token, resp.data);
  }, [login, token]);

  const resetState = useCallback(() => {
    setStep(STEPS.IDLE);
    setOtp(''); setPassword(''); setQrData(null);
    setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
  }, []);

  const handleSetup = useCallback(async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/setup`, {});
      setQrData(response.data);
      setStep(STEPS.SETUP);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start 2FA setup');
    } finally { setProcessing(false); }
  }, []);

  const handleVerify = useCallback(async () => {
    if (!otp || otp.length !== 6) { toast.error('Please enter the 6-digit code'); return; }
    setProcessing(true);
    try {
      await axios.post(`${API}/auth/2fa/verify`, { otp });
      toast.success('2FA enabled successfully!');
      await refreshUser();
      resetState();
      setOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally { setProcessing(false); }
  }, [otp, refreshUser, resetState]);

  const handleDisable = useCallback(async () => {
    if (!password) { toast.error('Please enter your password'); return; }
    setProcessing(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { password });
      toast.success('2FA disabled');
      await refreshUser();
      resetState();
      setOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable');
    } finally { setProcessing(false); }
  }, [password, refreshUser, resetState]);

  const handleChangePassword = useCallback(async () => {
    if (newPwd.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPwd !== confirmPwd) { toast.error('Passwords do not match'); return; }
    setProcessing(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPwd, new_password: newPwd });
      toast.success('Password changed successfully');
      resetState();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally { setProcessing(false); }
  }, [currentPwd, newPwd, confirmPwd, resetState]);

  const copySecret = useCallback(() => {
    navigator.clipboard.writeText(qrData.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [qrData]);

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetState(); }}>
      <DialogTrigger asChild>
        <Button data-testid="security-btn" variant="ghost" size="sm" className="rounded-full">
          {user?.twofa_enabled ? <ShieldCheck className="w-4 h-4 mr-2 text-green-600" /> : <ShieldOff className="w-4 h-4 mr-2 text-muted-foreground" />}
          Security
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Security Settings</DialogTitle></DialogHeader>

        {step === STEPS.IDLE && (
          <SecurityIdleStep
            user={user}
            processing={processing}
            onEnable2FA={handleSetup}
            onDisable2FA={() => setStep(STEPS.DISABLE)}
            onChangePassword={() => setStep(STEPS.CHANGE_PWD)}
          />
        )}

        {step === STEPS.CHANGE_PWD && (
          <ChangePasswordStep
            currentPwd={currentPwd} setCurrentPwd={setCurrentPwd}
            newPwd={newPwd} setNewPwd={setNewPwd}
            confirmPwd={confirmPwd} setConfirmPwd={setConfirmPwd}
            onSubmit={handleChangePassword}
            onCancel={() => setStep(STEPS.IDLE)}
            processing={processing}
          />
        )}

        {step === STEPS.SETUP && qrData && (
          <TwoFASetupStep
            qrData={qrData}
            otp={otp} setOtp={setOtp}
            copied={copied} onCopySecret={copySecret}
            onVerify={handleVerify}
            processing={processing}
          />
        )}

        {step === STEPS.DISABLE && (
          <TwoFADisableStep
            password={password} setPassword={setPassword}
            onConfirm={handleDisable}
            onCancel={() => setStep(STEPS.IDLE)}
            processing={processing}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
